import { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Camera, CameraOff, MessageSquare, Mic, MicOff, PhoneOff, RefreshCw, X } from "lucide-react";
import ProblemClarification from "@/components/ProblemClarification";

const durationLabel = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

export default function VideoCallRoom({ problemId, role, expertUserId, joinInfo, devices, onEnd, onLeave }) {
  const mountRef = useRef(null), frameRef = useRef(null);
  const [connection, setConnection] = useState("connecting"), [audioOn, setAudioOn] = useState(devices.microphoneOn !== false), [videoOn, setVideoOn] = useState(devices.cameraOn !== false);
  const [elapsed, setElapsed] = useState(0), [chatOpen, setChatOpen] = useState(false), [confirmEnd, setConfirmEnd] = useState(false), [ending, setEnding] = useState(false);

  useEffect(() => {
    const start = new Date(joinInfo.started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick(); const timer = setInterval(tick, 1000); return () => clearInterval(timer);
  }, [joinInfo.started_at]);

  useEffect(() => {
    if (!mountRef.current) return undefined;
    const frame = DailyIframe.createFrame(mountRef.current, {
      showLeaveButton: false,
      iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "0" },
    });
    frameRef.current = frame;
    frame.on("joining-meeting", () => setConnection("connecting"));
    frame.on("joined-meeting", () => setConnection("connected"));
    frame.on("left-meeting", () => { setConnection("disconnected"); onLeave?.(); });
    frame.on("error", () => setConnection("reconnecting"));
    frame.join({
      url: joinInfo.server_url,
      token: joinInfo.meeting_token,
      userName: joinInfo.user_name,
      startAudioOff: !devices.microphoneOn,
      startVideoOff: !devices.cameraOn,
    }).then(async () => {
      if (devices.cameraId || devices.microphoneId) {
        await frame.setInputDevicesAsync({
          ...(devices.cameraId ? { videoDevice: devices.cameraId } : {}),
          ...(devices.microphoneId ? { audioDevice: devices.microphoneId } : {}),
        }).catch(() => {});
      }
    }).catch(() => setConnection("reconnecting"));
    return () => { try { frame.destroy(); } catch { /* frame may already be gone after navigation */ } frameRef.current = null; };
  }, [devices.cameraId, devices.cameraOn, devices.microphoneId, devices.microphoneOn, joinInfo.meeting_token, joinInfo.server_url, joinInfo.user_name, onLeave]);

  const toggleAudio = () => { const next = !audioOn; frameRef.current?.setLocalAudio(next); setAudioOn(next); };
  const toggleVideo = () => { const next = !videoOn; frameRef.current?.setLocalVideo(next); setVideoOn(next); };
  const switchCamera = async () => {
    const cameras = (await navigator.mediaDevices?.enumerateDevices?.() || []).filter((device) => device.kind === "videoinput");
    if (cameras.length < 2) return;
    const current = frameRef.current?.getInputDevices?.()?.camera?.deviceId;
    const index = cameras.findIndex((camera) => camera.deviceId === current);
    await frameRef.current?.setInputDevicesAsync({ videoDevice: cameras[(index + 1) % cameras.length].deviceId }).catch(() => {});
  };
  const end = async () => { setEnding(true); try { await frameRef.current?.leave(); await onEnd(); } finally { setEnding(false); } };

  return <div className="video-call-shell" data-testid="embedded-video-call">
    <div className="video-call-stage"><div ref={mountRef} className="video-daily-frame"/>
      {connection !== "connected" && <div className="video-connection-state"><RefreshCw className="animate-spin"/><strong>{connection === "reconnecting" ? "Forbindelsen blev afbrudt" : "Forbinder…"}</strong>{connection === "reconnecting" && <span>Prøver at forbinde igen…</span>}</div>}
      <div className="video-elapsed" aria-label="Samtalens varighed">{durationLabel(elapsed)}</div>
      <div className="video-call-controls">
        <button onClick={toggleAudio} className={!audioOn ? "is-off" : ""} aria-label={audioOn ? "Slå mikrofon fra" : "Slå mikrofon til"}>{audioOn ? <Mic/> : <MicOff/>}<span>Mikrofon</span></button>
        <button onClick={toggleVideo} className={!videoOn ? "is-off" : ""} aria-label={videoOn ? "Slå kamera fra" : "Slå kamera til"}>{videoOn ? <Camera/> : <CameraOff/>}<span>Kamera</span></button>
        <button onClick={switchCamera} aria-label="Skift kamera"><RefreshCw/><span>Skift</span></button>
        <button onClick={() => setChatOpen(true)} aria-label="Åbn chat"><MessageSquare/><span>Chat</span></button>
        <button onClick={() => setConfirmEnd(true)} className="is-end" aria-label="Afslut samtale"><PhoneOff/><span>Afslut</span></button>
      </div>
    </div>
    {chatOpen && <aside className="video-chat-drawer"><header><div><strong>Chat om opgaven</strong><span>Beskeder og filer gemmes på forespørgslen</span></div><button onClick={() => setChatOpen(false)} aria-label="Luk chat"><X/></button></header><ProblemClarification problemId={problemId} role={role} expertUserId={role === "customer" ? expertUserId : undefined}/></aside>}
    {confirmEnd && <div className="video-end-backdrop" role="dialog" aria-modal="true"><div className="video-end-dialog"><PhoneOff/><h2>Vil du afslutte videosamtalen?</h2><p>Opgaven bliver ikke markeret som afsluttet.</p><div><button onClick={() => setConfirmEnd(false)} className="btn-secondary">Fortsæt samtalen</button><button onClick={end} disabled={ending} className="video-end-confirm">{ending ? "Afslutter…" : "Afslut"}</button></div></div></div>}
  </div>;
}
